import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Header,
  Form,
  FormField,
  Input,
  Button,
  SpaceBetween,
  Alert,
  Tabs,
  StatusIndicator,
  ColumnLayout,
  Box,
  CopyToClipboard,
  BreadcrumbGroup,
  Link,
} from "@cloudscape-design/components";
import { useAuth } from "../AuthContext";
import { useNotifications } from "./NotificationContext";
import { AUTH_API } from "../config/api";
import AppLayoutWrapper from "./AppLayoutWrapper";

const SecuritySettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  const accessToken = user?.access_token || user?.token;

  // Change Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // TOTP state
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpSecret, setTotpSecret] = useState(null);
  const [totpQrUrl, setTotpQrUrl] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [disablingCode, setDisablingCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [loadingTOTP, setLoadingTOTP] = useState(false);
  const [checkingTOTP, setCheckingTOTP] = useState(true);
  const [activeTab, setActiveTab] = useState("password");

  const fetchTotpStatus = useCallback(async () => {
    if (!accessToken) return;
    try {
      setCheckingTOTP(true);
      const res = await fetch(AUTH_API.totpSetup, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (res.ok) {
        setTotpEnabled(data.enabled || false);
        if (data.secret && data.qr_url) {
          setTotpSecret(data.secret);
          setTotpQrUrl(data.qr_url);
        }
      }
    } catch (err) {
      console.error("Error fetching TOTP status:", err);
    } finally {
      setCheckingTOTP(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchTotpStatus();
  }, [fetchTotpStatus]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      notifyError("Veuillez remplir tous les champs.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      notifyError("Les nouveaux mots de passe ne correspondent pas.");
      return;
    }
    if (newPassword.length < 8) {
      notifyError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    try {
      setChangingPassword(true);
      const res = await fetch(AUTH_API.changePassword, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        notifySuccess("Mot de passe modifié avec succès.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
      } else {
        notifyError(data.message || "Erreur lors du changement de mot de passe.");
      }
    } catch (err) {
      notifyError("Erreur réseau.");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSetupTOTP = async (e) => {
    e.preventDefault();
    if (!setupCode || setupCode.length < 6) {
      notifyError("Entrez le code à 6 chiffres.");
      return;
    }
    try {
      setLoadingTOTP(true);
      const res = await fetch(AUTH_API.totpSetup, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ code: setupCode }),
      });
      const data = await res.json();
      if (res.ok) {
        notifySuccess("2FA activé avec succès.");
        setTotpEnabled(true);
        if (data.backup_codes) {
          setBackupCodes(data.backup_codes);
        }
        setSetupCode("");
      } else {
        notifyError(data.error || "Code invalide.");
      }
    } catch (err) {
      notifyError("Erreur réseau.");
    } finally {
      setLoadingTOTP(false);
    }
  };

  const handleDisableTOTP = async (e) => {
    e.preventDefault();
    if (!disablingCode || disablingCode.length < 6) {
      notifyError("Entrez le code à 6 chiffres.");
      return;
    }
    try {
      setLoadingTOTP(true);
      const res = await fetch(AUTH_API.totpDisable, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ code: disablingCode }),
      });
      const data = await res.json();
      if (res.ok) {
        notifySuccess("2FA désactivé.");
        setTotpEnabled(false);
        setTotpSecret(null);
        setTotpQrUrl("");
        setBackupCodes([]);
        setDisablingCode("");
      } else {
        notifyError(data.error || "Code invalide.");
      }
    } catch (err) {
      notifyError("Erreur réseau.");
    } finally {
      setLoadingTOTP(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    try {
      setLoadingTOTP(true);
      const res = await fetch(AUTH_API.totpBackupCodes, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await res.json();
      if (res.ok && data.backup_codes) {
        setBackupCodes(data.backup_codes);
        notifySuccess("Codes de secours régénérés.");
      } else {
        notifyError(data.error || "Impossible de régénérer les codes.");
      }
    } catch (err) {
      notifyError("Erreur réseau.");
    } finally {
      setLoadingTOTP(false);
    }
  };

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: "Home", href: "/dashboard" },
            { text: "Security", href: "/security" },
          ]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: "Security",
        description: "Manage your password and two-factor authentication to keep your Adexgénie account secure.",
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
    >
      <SpaceBetween size="l">
        <ColumnLayout columns={{ default: 1, s: 1, m: 2, l: 2, xl: 2 }} variant="text-grid">
            <Container header={<Header variant="h2" description="Your login credentials">Password</Header>}>
              <SpaceBetween size="s">
                <StatusIndicator type="success">Password set</StatusIndicator>
                <Box variant="small" color="text-body-secondary">
                  Last changed: {user?.last_password_change ? new Date(user.last_password_change).toLocaleDateString() : 'Unknown'}
                </Box>
                <Link variant="info" onFollow={() => setActiveTab("password")}>
                  Change password
                </Link>
              </SpaceBetween>
            </Container>
            <Container header={<Header variant="h2" description="Extra layer of security for your account">Two-factor authentication</Header>}>
              <SpaceBetween size="s">
                {checkingTOTP ? (
                  <StatusIndicator type="loading">Loading</StatusIndicator>
                ) : totpEnabled ? (
                  <StatusIndicator type="success">Enabled</StatusIndicator>
                ) : (
                  <StatusIndicator type="pending">Not enabled</StatusIndicator>
                )}
                <Box variant="small" color="text-body-secondary">
                  2FA adds a verification code on top of your password for enhanced security.
                </Box>
                <Link variant="info" onFollow={() => setActiveTab("2fa")}>
                  {totpEnabled ? "Manage 2FA" : "Enable 2FA"}
                </Link>
              </SpaceBetween>
            </Container>
          </ColumnLayout>

        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
          tabs={[
            {
              id: "password",
              label: "Password",
              content: (
                <Container header={<Header variant="h2" description="Choose a strong password with at least 8 characters">Change password</Header>}>
                  <form onSubmit={handleChangePassword} style={{ maxWidth: 500 }}>
                    <Form
                      actions={
                        <Button variant="primary" loading={changingPassword} formAction="submit">
                          Change password
                        </Button>
                      }
                    >
                      <FormField label="Current password">
                        <Input
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.detail.value)}
                        />
                      </FormField>
                      <FormField label="New password">
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.detail.value)}
                        />
                      </FormField>
                      <FormField label="Confirm new password">
                        <Input
                          type="password"
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.detail.value)}
                        />
                      </FormField>
                    </Form>
                  </form>
                </Container>
              ),
            },
            {
              id: "2fa",
              label: "Two-factor authentication",
              content: (
                <SpaceBetween size="l">
                  {checkingTOTP ? (
                    <StatusIndicator type="loading">Loading...</StatusIndicator>
                  ) : totpEnabled ? (
                    <Container header={<Header variant="h2" description="Scan the QR code with an authenticator app (Google Authenticator, Authy, etc.)">2FA enabled</Header>}>
                      <Alert type="success" header="Two-factor authentication is active">Your account is protected with an additional verification layer.</Alert>
                      <SpaceBetween size="m">
                        <form onSubmit={handleDisableTOTP} style={{ maxWidth: 500 }}>
                          <Form
                            actions={
                              <Button variant="primary" loading={loadingTOTP} formAction="submit">
                                Disable 2FA
                              </Button>
                            }
                          >
                            <FormField label="Current TOTP code">
                              <Input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={disablingCode}
                                onChange={(e) => setDisablingCode(e.detail.value.replace(/\D/g, ""))}
                                placeholder="000000"
                              />
                            </FormField>
                          </Form>
                        </form>
                        {backupCodes.length > 0 && (
                          <Container header={<Header variant="h3">Backup codes</Header>}>
                            <SpaceBetween size="s">
                              <Alert type="warning">
                                Keep these codes safe. They will only be shown once.
                              </Alert>
                              <ColumnLayout columns={2}>
                                {backupCodes.map((code, i) => (
                                  <Box key={i} fontWeight="bold">{code}</Box>
                                ))}
                              </ColumnLayout>
                            </SpaceBetween>
                          </Container>
                        )}
                        <Button onClick={handleRegenerateBackupCodes} loading={loadingTOTP}>
                          Regenerate backup codes
                        </Button>
                      </SpaceBetween>
                    </Container>
                  ) : (
                    <Container header={<Header variant="h2" description="Protect your account with TOTP-based two-factor authentication">Enable 2FA</Header>}>
                      <SpaceBetween size="m">
                        {totpQrUrl && (
                          <Box textAlign="center">
                            <img src={totpQrUrl} alt="QR Code TOTP" style={{ maxWidth: 200 }} />
                          </Box>
                        )}
                        {totpSecret && (
                          <Box textAlign="center">
                            <CopyToClipboard
                              textToCopy={totpSecret}
                              copyButtonText="Copy secret key"
                            />
                          </Box>
                        )}
                        <form onSubmit={handleSetupTOTP} style={{ maxWidth: 500 }}>
                          <Form
                            actions={
                              <Button variant="primary" loading={loadingTOTP} formAction="submit">
                                Enable 2FA
                              </Button>
                            }
                          >
                            <FormField label="Verification code (6 digits)">
                              <Input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={setupCode}
                                onChange={(e) => setSetupCode(e.detail.value.replace(/\D/g, ""))}
                                placeholder="000000"
                              />
                            </FormField>
                          </Form>
                        </form>
                      </SpaceBetween>
                    </Container>
                  )}
                </SpaceBetween>
              ),
            },
          ]}
        />
      </SpaceBetween>
    </AppLayoutWrapper>
  );
};

export default SecuritySettings;
